import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	Image,
	ImageSourcePropType,
	LayoutChangeEvent,
	StyleSheet,
	Text,
	View,
	ViewStyle,
} from 'react-native';
import Svg, {
	Defs,
	FeBlend,
	FeColorMatrix,
	FeFlood,
	FeGaussianBlur,
	FeOffset,
	Filter,
	G,
	LinearGradient,
	Path,
	Stop,
} from 'react-native-svg';

import {
	DuelHudChevronLeftSvg,
	DuelHudChevronRightSvg,
	DuelHudClockPng,
	DuelHudFinishedFlagSvg,
	DuelHudHistorySvg,
	DuelHudInfoSvg,
	DuelHudLeaveSvg,
	DuelHudLiveDotSvg,
	DuelHudPeoplePng,
	DuelHudRailShellSvg,
	DuelHudSheetShellSvg,
	DuelHudShieldPng,
	DuelHudTrophyPng,
} from '../../../assets/duels';
import type { DuelSnapshotDto } from '../../../services/api';
import { useDevice } from '../../../utils/hooks/useDevice';
import { useSvgId } from '../../../utils/hooks/useSvgId';
import { GradientFill } from '../common/GradientFill';
import { Pressable, TouchableOpacity } from '../common/Touchable';
import RedesignConfirmModal from '../Modals/RedesignConfirmModal';
import { useCountdown } from '../Tournaments/useCountdown';
import {
	DUEL_HUD_COUNTER_FILL,
	DUEL_HUD_DESKTOP_MIN_HEIGHT,
	DUEL_HUD_DESKTOP_SCALE,
	DUEL_HUD_FONT,
	DUEL_HUD_HISTORY_FILL,
	DUEL_HUD_LEAVE_FILL,
	DUEL_HUD_MENU,
	DUEL_HUD_PORTRAIT,
	DUEL_HUD_RAIL,
	DUEL_HUD_SHELL_FILL,
	DUEL_HUD_SHELL_MARGIN,
	DUEL_MODAL_ORIENTATIONS,
	duelHudColors,
	duelHudCounts,
	duelHudDeadline,
	formatDuelHudCountdown,
} from './duelHudTheme';
import { DuelMoreMenu, DuelMoreMenuItem } from './DuelMoreMenu';
import { duelText } from './duelsText';

export const useDuelHudLayout = () => {
	const { width, height, isLandscape, insets } = useDevice();
	const desktop = isLandscape && height >= DUEL_HUD_DESKTOP_MIN_HEIGHT;
	return {
		width,
		height,
		insets,
		landscape: isLandscape,
		phoneLandscape: isLandscape && !desktop,
		scale: desktop ? DUEL_HUD_DESKTOP_SCALE : 1,
	};
};

type ArtInsets = { top: number; right: number; bottom: number; left: number };

const ART = {
	trophy: { top: 0.1326, right: 0.1139, bottom: 0.0953, left: 0.1139 },
	people: { top: 0.0881, right: 0.0295, bottom: 0.0769, left: 0.0295 },
	peopleLarge: { top: 0.0728, right: 0.0295, bottom: 0.0619, left: 0.0295 },
	clock: { top: 0.058, right: 0.0603, bottom: 0.0625, left: 0.0603 },
	shield: { top: 0.0585, right: 0.0771, bottom: 0.0934, left: 0.0749 },
} satisfies Record<string, ArtInsets>;

const ArtIcon = ({
	source,
	width,
	height,
	bleed,
}: {
	source: ImageSourcePropType;
	width: number;
	height: number;
	bleed: ArtInsets;
}) => (
	<View style={{ width, height }}>
		<Image
			source={source}
			resizeMode='cover'
			style={{
				position: 'absolute',
				left: -bleed.left * width,
				top: -bleed.top * height,
				width: width * (1 + bleed.left + bleed.right),
				height: height * (1 + bleed.top + bleed.bottom),
			}}
		/>
	</View>
);

const HudDivider = ({ width, vertical }: { width: number; vertical?: boolean }) => (
	<View style={vertical ? { width: 1, height: width } : { width, height: 1 }}>
		<GradientFill
			direction={vertical ? 'vertical' : 'horizontal'}
			stops={[
				['0', duelHudColors.dividerClear],
				['0.5', duelHudColors.divider],
				['1', duelHudColors.dividerClear],
			]}
		/>
	</View>
);

const HudStat = ({
	icon,
	value,
	caption,
	valueColor,
	s,
	horizontal,
}: {
	icon: React.ReactNode;
	value: string;
	caption: string;
	valueColor: string;
	s: number;
	horizontal?: boolean;
}) => (
	<View
		style={[
			horizontal ? styles.row : styles.column,
			{ columnGap: 4 * s, rowGap: 4 * s, alignItems: 'center' },
		]}
	>
		{icon}
		<View style={{ alignItems: horizontal ? 'flex-start' : 'center' }}>
			<Text
				numberOfLines={1}
				style={[styles.bold, { color: valueColor, fontSize: 14 * s, lineHeight: 16 * s }]}
			>
				{value}
			</Text>
			<Text
				numberOfLines={1}
				style={[
					styles.medium,
					{ color: duelHudColors.textSecondary, fontSize: 12 * s, lineHeight: 14 * s },
				]}
			>
				{caption}
			</Text>
		</View>
	</View>
);

const HudStatus = ({ s, horizontal }: { s: number; horizontal?: boolean }) => {
	const { t } = useTranslation();
	return (
		<View
			style={[
				horizontal ? styles.row : styles.column,
				{ columnGap: 4 * s, rowGap: 4 * s, alignItems: 'center' },
			]}
		>
			<ArtIcon source={DuelHudTrophyPng} width={28 * s} height={28 * s} bleed={ART.trophy} />
			<View style={{ alignItems: horizontal ? 'flex-start' : 'center' }}>
				<Text
					numberOfLines={1}
					style={[
						styles.black,
						{
							color: duelHudColors.textPrimary,
							fontSize: 14 * s,
							lineHeight: 16 * s,
							letterSpacing: 0.84 * s,
						},
					]}
				>
					{duelText(t, 'T_SITE_DUELS_HUD_DUEL')}
				</Text>
				<Text
					numberOfLines={1}
					style={[
						styles.semibold,
						{ color: duelHudColors.success, fontSize: 12 * s, lineHeight: 14 * s },
					]}
				>
					{duelText(t, 'T_SITE_DUELS_HUD_IN_PROGRESS')}
				</Text>
			</View>
		</View>
	);
};

const HudTime = ({
	duel,
	s,
	horizontal,
}: {
	duel: DuelSnapshotDto;
	s: number;
	horizontal?: boolean;
}) => {
	const { t } = useTranslation();
	const remaining = useCountdown(duelHudDeadline(duel));
	return (
		<HudStat
			s={s}
			horizontal={horizontal}
			icon={<ArtIcon source={DuelHudClockPng} width={20 * s} height={20 * s} bleed={ART.clock} />}
			value={formatDuelHudCountdown(remaining)}
			caption={duelText(t, 'T_SITE_DUELS_HUD_LEFT')}
			valueColor={duelHudColors.time}
		/>
	);
};

const HudPlayers = ({
	duel,
	s,
	horizontal,
}: {
	duel: DuelSnapshotDto;
	s: number;
	horizontal?: boolean;
}) => {
	const { t } = useTranslation();
	const counts = duelHudCounts(duel);
	return (
		<HudStat
			s={s}
			horizontal={horizontal}
			icon={<ArtIcon source={DuelHudPeoplePng} width={22 * s} height={20 * s} bleed={ART.people} />}
			value={`${counts.players}/${counts.maxPlayers}`}
			caption={duelText(t, 'T_SITE_DUELS_HUD_PLAYERS')}
			valueColor={duelHudColors.players}
		/>
	);
};

const HudHistoryButton = ({
	s,
	horizontal,
	compact = false,
	onPress,
}: {
	s: number;
	horizontal?: boolean;
	compact?: boolean;
	onPress: () => void;
}) => {
	const { t } = useTranslation();
	const radius = 12 * s;
	return (
		<TouchableOpacity
			onPress={onPress}
			accessibilityRole='button'
			accessibilityLabel={duelText(t, 'T_SITE_DUELS_HUD_HISTORY')}
			style={[
				horizontal ? styles.row : styles.column,
				styles.centered,
				{
					borderRadius: radius,
					borderWidth: Math.max(1, s),
					borderColor: duelHudColors.historyBorder,
					columnGap: 6 * s,
					rowGap: 2 * s,
					paddingHorizontal: (compact ? 7 : horizontal ? 10 : 4) * s,
					paddingVertical: horizontal ? 0 : 8 * s,
					height: horizontal ? DUEL_HUD_PORTRAIT.buttonHeight * s : undefined,
					alignSelf: horizontal ? 'center' : 'stretch',
				},
			]}
		>
			<GradientFill stops={DUEL_HUD_HISTORY_FILL} radius={radius} />
			<View pointerEvents='none'>
				<DuelHudHistorySvg width={20 * s} height={20 * s} />
			</View>
			{compact ? null : (
				<Text
					numberOfLines={1}
					style={[
						styles.semibold,
						{ color: duelHudColors.textPrimary, fontSize: 12 * s, lineHeight: 16 * s },
					]}
				>
					{duelText(t, 'T_SITE_DUELS_HUD_HISTORY')}
				</Text>
			)}
		</TouchableOpacity>
	);
};

const HudCounter = ({
	icon,
	value,
	caption,
	s,
}: {
	icon: React.ReactNode;
	value: number;
	caption: string;
	s: number;
}) => {
	const radius = 12 * s;
	return (
		<View
			style={[
				styles.row,
				styles.counter,
				{
					borderRadius: radius,
					borderWidth: Math.max(1, s),
					borderColor: duelHudColors.counterBorder,
					columnGap: 12 * s,
					padding: 12 * s,
				},
			]}
		>
			<GradientFill stops={DUEL_HUD_COUNTER_FILL} radius={radius} />
			{icon}
			<View>
				<Text
					numberOfLines={1}
					style={[
						styles.black,
						{ color: duelHudColors.counter, fontSize: 24 * s, lineHeight: 28 * s },
					]}
				>
					{value}
				</Text>
				<Text
					numberOfLines={1}
					style={[
						styles.medium,
						{ color: duelHudColors.textSecondary, fontSize: 12 * s, lineHeight: 16 * s },
					]}
				>
					{caption}
				</Text>
			</View>
		</View>
	);
};

type DrawerBodyProps = {
	duel: DuelSnapshotDto;
	s: number;
	pillBelowTitle: boolean;
	leaving: boolean;
	leaveErrorKey?: string;
	onLeave: () => void;
	onInfo: () => void;
};

const HudDrawerBody = ({
	duel,
	s,
	pillBelowTitle,
	leaving,
	leaveErrorKey,
	onLeave,
	onInfo,
}: DrawerBodyProps) => {
	const { t } = useTranslation();
	const counts = duelHudCounts(duel);
	const leaveRadius = 12 * s;

	const pill = (
		<View
			style={[
				styles.row,
				styles.pill,
				{
					height: 24 * s,
					columnGap: 6 * s,
					paddingHorizontal: 8 * s,
					borderWidth: Math.max(1, s),
				},
			]}
		>
			<View style={{ width: 8 * s, height: 8 * s }}>
				<View style={{ position: 'absolute', left: -6 * s, top: -6 * s }}>
					<DuelHudLiveDotSvg width={20 * s} height={20 * s} />
				</View>
			</View>
			<Text
				style={[
					styles.bold,
					{
						color: duelHudColors.success,
						fontSize: 12 * s,
						lineHeight: 16 * s,
						letterSpacing: 0.72 * s,
					},
				]}
			>
				{duelText(t, 'T_SITE_DUELS_HUD_PLAYING')}
			</Text>
		</View>
	);

	return (
		<View style={{ rowGap: 12 * s }}>
			<View
				style={[
					pillBelowTitle ? styles.column : styles.row,
					{
						columnGap: 12 * s,
						rowGap: 8 * s,
						alignItems: pillBelowTitle ? 'flex-start' : 'center',
					},
				]}
			>
				<Text
					numberOfLines={1}
					style={[
						styles.black,
						styles.shrink,
						{
							color: duelHudColors.textPrimary,
							fontSize: 20 * s,
							lineHeight: 24 * s,
							letterSpacing: 0.4 * s,
						},
					]}
				>
					{`${duelText(t, 'T_SITE_DUELS_HUD_RUN_TITLE')} `}
					<Text style={[styles.blackItalic, { color: duelHudColors.success }]}>
						{duelText(t, 'T_SITE_DUELS_HUD_RUN_LIVE')}
					</Text>
				</Text>
				{pill}
			</View>
			<Text
				style={[
					styles.medium,
					{ color: duelHudColors.textSecondary, fontSize: 14 * s, lineHeight: 20 * s },
				]}
			>
				{duelText(t, 'T_SITE_DUELS_HUD_RUN_BODY')}
			</Text>
			<View style={[styles.row, { columnGap: 8 * s }]}>
				<HudCounter
					s={s}
					value={counts.finished}
					caption={duelText(t, 'T_SITE_DUELS_HUD_FINISHED')}
					icon={
						<View style={{ width: 28 * s, height: 28 * s }}>
							<View style={{ position: 'absolute', left: -2.97 * s, top: -4.72 * s }}>
								<DuelHudFinishedFlagSvg width={33.4667 * s} height={36.3833 * s} />
							</View>
						</View>
					}
				/>
				<HudCounter
					s={s}
					value={counts.stillPlaying}
					caption={duelText(t, 'T_SITE_DUELS_HUD_STILL_PLAYING')}
					icon={
						<ArtIcon
							source={DuelHudPeoplePng}
							width={30 * s}
							height={28 * s}
							bleed={ART.peopleLarge}
						/>
					}
				/>
			</View>
			<Pressable
				onPress={onInfo}
				accessibilityRole='button'
				style={[styles.row, styles.centered, { columnGap: 8 * s }]}
			>
				<ArtIcon source={DuelHudShieldPng} width={16 * s} height={16 * s} bleed={ART.shield} />
				<Text
					numberOfLines={1}
					style={[
						styles.medium,
						{ color: duelHudColors.textSecondary, fontSize: 12 * s, lineHeight: 16 * s },
					]}
				>
					{duelText(t, 'T_SITE_DUELS_HUD_TRUST')}
				</Text>
				<DuelHudInfoSvg width={16 * s} height={16 * s} />
			</Pressable>
			<TouchableOpacity
				onPress={onLeave}
				disabled={leaving}
				accessibilityRole='button'
				accessibilityState={{ disabled: leaving, busy: leaving }}
				style={[
					styles.row,
					styles.centered,
					styles.leave,
					{
						height: 48 * s,
						borderRadius: leaveRadius,
						borderWidth: Math.max(1, s),
						columnGap: 8 * s,
						shadowRadius: 6 * s,
						opacity: leaving ? 0.6 : 1,
					},
				]}
			>
				<GradientFill stops={DUEL_HUD_LEAVE_FILL} radius={leaveRadius} />
				<View pointerEvents='none'>
					<DuelHudLeaveSvg width={20 * s} height={20 * s} />
				</View>
				<Text
					style={[
						styles.bold,
						{
							color: duelHudColors.textPrimary,
							fontSize: 14 * s,
							lineHeight: 16 * s,
							letterSpacing: 0.84 * s,
						},
					]}
				>
					{duelText(t, 'T_SITE_DUELS_HUD_LEAVE')}
				</Text>
			</TouchableOpacity>
			{leaveErrorKey ? (
				<Text
					style={[
						styles.semibold,
						styles.centeredText,
						{ color: duelHudColors.time, fontSize: 12 * s, lineHeight: 16 * s },
					]}
				>
					{duelText(t, leaveErrorKey)}
				</Text>
			) : null}
		</View>
	);
};

const portraitShellPath = (w: number, h: number) => {
	const m = DUEL_HUD_SHELL_MARGIN;
	const r = DUEL_HUD_PORTRAIT.radius;
	const tab = DUEL_HUD_PORTRAIT.tab;
	const left = m.left;
	const top = m.top;
	const right = m.left + w;
	const bottom = m.top + h;
	const mid = m.left + w / 2;
	return [
		`M${left + r} ${top}`,
		`H${right - r}`,
		`C${right - r + 8.84} ${top} ${right} ${top + 7.16} ${right} ${top + r}`,
		`V${bottom - r}`,
		`C${right} ${bottom - 7.16} ${right - r + 8.84} ${bottom} ${right - r} ${bottom}`,
		`H${mid + 36}`,
		`C${mid + 25} ${bottom} ${mid + 25} ${bottom + tab} ${mid + 16} ${bottom + tab}`,
		`H${mid - 16}`,
		`C${mid - 25} ${bottom + tab} ${mid - 25} ${bottom} ${mid - 36} ${bottom}`,
		`H${left + r}`,
		`C${left + 7.16} ${bottom} ${left} ${bottom - 7.16} ${left} ${bottom - r}`,
		`V${top + r}`,
		`C${left} ${top + 7.16} ${left + 7.16} ${top} ${left + r} ${top}`,
		'Z',
	].join(' ');
};

const PortraitShell = ({ width, height }: { width: number; height: number }) => {
	const fillId = useSvgId('duelHudFill');
	const sheenId = useSvgId('duelHudSheen');
	const glowId = useSvgId('duelHudGlow');
	const m = DUEL_HUD_SHELL_MARGIN;
	const svgWidth = width + m.left + m.right;
	const svgHeight = height + m.top + m.bottom;
	const path = portraitShellPath(width, height);

	return (
		<View pointerEvents='none' style={{ position: 'absolute', left: -m.left, top: -m.top }}>
			<Svg width={svgWidth} height={svgHeight}>
				<Defs>
					<LinearGradient
						id={fillId}
						x1='0'
						y1={m.top}
						x2='0'
						y2={m.top + height}
						gradientUnits='userSpaceOnUse'
					>
						{DUEL_HUD_SHELL_FILL.map(([offset, color]) => (
							<Stop key={offset} offset={offset} stopColor={color} />
						))}
					</LinearGradient>
					<LinearGradient
						id={sheenId}
						x1='0'
						y1={m.top}
						x2='0'
						y2={m.top + height}
						gradientUnits='userSpaceOnUse'
					>
						<Stop offset='0' stopColor='#FFFFFF' stopOpacity={0.06} />
						<Stop offset='0.45' stopColor='#FFFFFF' stopOpacity={0} />
					</LinearGradient>
					<Filter
						id={glowId}
						x='0'
						y='0'
						width={svgWidth}
						height={svgHeight}
						filterUnits='userSpaceOnUse'
					>
						<FeFlood floodOpacity='0' result='BackgroundImageFix' />
						<FeColorMatrix
							in='SourceAlpha'
							type='matrix'
							values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
							result='hardAlpha'
						/>
						<FeOffset dy='8' />
						<FeGaussianBlur stdDeviation='10' />
						<FeColorMatrix
							type='matrix'
							values='0 0 0 0 0.00392157 0 0 0 0 0.0235294 0 0 0 0 0.0509804 0 0 0 0.55 0'
						/>
						<FeBlend mode='normal' in2='BackgroundImageFix' result='dropShadow' />
						<FeColorMatrix
							in='SourceAlpha'
							type='matrix'
							values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
							result='hardAlpha'
						/>
						<FeOffset />
						<FeGaussianBlur stdDeviation='8' />
						<FeColorMatrix
							type='matrix'
							values='0 0 0 0 0 0 0 0 0 0.658824 0 0 0 0 1 0 0 0 0.45 0'
						/>
						<FeBlend mode='normal' in2='dropShadow' result='glow' />
						<FeBlend mode='normal' in='SourceGraphic' in2='glow' result='shape' />
					</Filter>
				</Defs>
				<G filter={`url(#${glowId})`}>
					<Path d={path} fill={`url(#${fillId})`} />
					<Path d={path} fill={`url(#${sheenId})`} />
					<Path d={path} stroke={duelHudColors.stroke} strokeWidth={1} fill='none' />
				</G>
			</Svg>
		</View>
	);
};

type DuelHudProps = {
	duel: DuelSnapshotDto;
	open: boolean;
	onToggle: () => void;
	onHistory: () => void;
	onLeave: () => void;
	leaving: boolean;
	leaveErrorKey?: string;
};

type DuelHudMenuProps = {
	menuOpen: boolean;
	onMenuToggle: () => void;
	onMenuClose: () => void;
	soundOn: boolean;
	unreadCount: number;
	onSound: () => void;
	onGameSettings: () => void;
	onNotifications: () => void;
};

const PortraitHud = (props: DuelHudProps & { layout: ReturnType<typeof useDuelHudLayout> }) => {
	const { duel, open, onToggle, onHistory, layout } = props;
	const p = DUEL_HUD_PORTRAIT;
	const width = Math.min(layout.width - 2 * p.side, p.width);
	const [bodyHeight, setBodyHeight] = useState<number>(p.barHeight);
	const [infoOpen, setInfoOpen] = useState(false);
	const { t } = useTranslation();

	const onBodyLayout = (event: LayoutChangeEvent) => {
		const next = Math.round(event.nativeEvent.layout.height);
		if (next !== bodyHeight) setBodyHeight(next);
	};

	return (
		<View
			pointerEvents='box-none'
			style={{
				position: 'absolute',
				top: layout.insets.top + p.topGap,
				left: (layout.width - width) / 2,
				width,
			}}
		>
			<PortraitShell width={width} height={bodyHeight} />
			<View onLayout={onBodyLayout}>
				<View
					style={[
						styles.row,
						{ height: p.barHeight, paddingLeft: p.inset * 2, columnGap: p.columnGap },
					]}
				>
					<Pressable
						onPress={onToggle}
						accessibilityRole='button'
						aria-expanded={open}
						style={[styles.row, styles.grow, { columnGap: p.columnGap, height: p.barHeight }]}
					>
						<HudStatus s={1} horizontal />
						<HudPlayers duel={duel} s={1} horizontal />
						<HudTime duel={duel} s={1} horizontal />
					</Pressable>
					<View style={[styles.row, { columnGap: p.inset }]}>
						<HudHistoryButton
							s={1}
							horizontal
							compact={width < DUEL_HUD_PORTRAIT.compactBelow}
							onPress={onHistory}
						/>
						<View style={{ width: DUEL_HUD_MENU.portraitSlot }} />
					</View>
				</View>
				{open ? (
					<View
						style={{
							paddingHorizontal: p.drawerPadding,
							paddingTop: p.drawerGap,
							paddingBottom: p.drawerPadding,
						}}
					>
						<HudDrawerBody
							duel={duel}
							s={1}
							pillBelowTitle
							leaving={props.leaving}
							leaveErrorKey={props.leaveErrorKey}
							onLeave={props.onLeave}
							onInfo={() => setInfoOpen(true)}
						/>
					</View>
				) : null}
			</View>
			<Pressable
				onPress={onToggle}
				accessibilityRole='button'
				aria-expanded={open}
				style={[styles.centered, { alignSelf: 'center', width: 72, height: p.tab }]}
			>
				<View style={{ transform: [{ rotate: open ? '-90deg' : '90deg' }] }}>
					<DuelHudChevronRightSvg width={16} height={16} />
				</View>
			</Pressable>
			{infoOpen ? (
				<RedesignConfirmModal
					message={duelText(t, 'T_SITE_DUELS_HUD_TRUST_INFO')}
					confirmLabel={duelText(t, 'T_SITE_DUELS_OK')}
					supportedOrientations={DUEL_MODAL_ORIENTATIONS}
					onCancel={() => setInfoOpen(false)}
					onConfirm={() => setInfoOpen(false)}
				/>
			) : null}
		</View>
	);
};

const RailHud = (props: DuelHudProps & { layout: ReturnType<typeof useDuelHudLayout> }) => {
	const { duel, open, onToggle, onHistory, layout } = props;
	const r = DUEL_HUD_RAIL;
	const s = layout.scale;
	const m = DUEL_HUD_SHELL_MARGIN;
	const [infoOpen, setInfoOpen] = useState(false);
	const { t } = useTranslation();
	const desktop = !layout.phoneLandscape;
	const left = desktop ? r.desktopLeft : Math.max(layout.insets.left, r.phoneLeft);
	const top = Math.round(layout.height * (desktop ? r.desktopTopRatio : r.phoneTopRatio));
	const sheetWidth = desktop ? r.desktopSheetWidth : r.sheetWidth * s;
	const width = open ? sheetWidth : r.width * s;
	const height = desktop ? r.desktopHeight : r.height * s;
	const Shell = open ? DuelHudSheetShellSvg : DuelHudRailShellSvg;
	const Chevron = open ? DuelHudChevronLeftSvg : DuelHudChevronRightSvg;

	return (
		<View pointerEvents='box-none' style={{ position: 'absolute', left, top, width, height }}>
			<View
				pointerEvents='none'
				style={{ position: 'absolute', left: -m.left * s, top: -m.top * s }}
			>
				<Shell width={width + (m.left + m.right) * s} height={height + (m.top + m.bottom) * s} />
			</View>
			<View
				pointerEvents='none'
				style={[
					styles.edgeLight,
					{ left: -1 * s, top: r.edgeTop * s, width: 3 * s, height: 40 * s, borderRadius: 1.5 * s },
				]}
			/>
			<View style={[styles.row, { height }]}>
				<View
					style={{
						width: r.columnWidth * s,
						height,
						paddingTop: 12 * s,
						paddingBottom: 4 * s,
						paddingHorizontal: 4 * s,
						rowGap: 8 * s,
						alignItems: 'center',
					}}
				>
					<Pressable
						onPress={onToggle}
						accessibilityRole='button'
						aria-expanded={open}
						style={[styles.column, styles.grow, { rowGap: 8 * s, alignItems: 'center' }]}
					>
						<HudStatus s={s} />
						<HudDivider width={40 * s} />
						<HudPlayers duel={duel} s={s} />
						<HudDivider width={40 * s} />
						<HudTime duel={duel} s={s} />
					</Pressable>
					<HudHistoryButton s={s} onPress={onHistory} />
				</View>
				{open ? (
					<>
						<View style={{ position: 'absolute', left: r.columnWidth * s, top: 16 * s }}>
							<HudDivider width={252 * s} vertical />
						</View>
						<View
							style={{
								width: width - (r.columnWidth + r.tab) * s,
								height,
								padding: r.bodyPadding * s,
								justifyContent: 'center',
							}}
						>
							<HudDrawerBody
								duel={duel}
								s={s}
								pillBelowTitle={false}
								leaving={props.leaving}
								leaveErrorKey={props.leaveErrorKey}
								onLeave={props.onLeave}
								onInfo={() => setInfoOpen(true)}
							/>
						</View>
					</>
				) : null}
			</View>
			<Pressable
				onPress={onToggle}
				accessibilityRole='button'
				aria-expanded={open}
				style={[
					styles.centered,
					{
						position: 'absolute',
						left: width - r.tab * s,
						top: r.edgeTop * s,
						width: r.tab * s,
						height: 72 * s,
					},
				]}
			>
				<Chevron width={16 * s} height={16 * s} />
			</Pressable>
			{infoOpen ? (
				<RedesignConfirmModal
					message={duelText(t, 'T_SITE_DUELS_HUD_TRUST_INFO')}
					confirmLabel={duelText(t, 'T_SITE_DUELS_OK')}
					supportedOrientations={DUEL_MODAL_ORIENTATIONS}
					onCancel={() => setInfoOpen(false)}
					onConfirm={() => setInfoOpen(false)}
				/>
			) : null}
		</View>
	);
};

export const DuelHud = ({
	menuOpen,
	onMenuToggle,
	onMenuClose,
	soundOn,
	unreadCount,
	onSound,
	onGameSettings,
	onNotifications,
	...props
}: DuelHudProps & DuelHudMenuProps) => {
	const layout = useDuelHudLayout();
	const p = DUEL_HUD_PORTRAIT;
	const m = DUEL_HUD_MENU;

	let menuStyle: ViewStyle;
	let plate: number;
	let gap: number;
	let items: DuelMoreMenuItem[];
	if (!layout.landscape) {
		const barWidth = Math.min(layout.width - 2 * p.side, p.width);
		menuStyle = {
			position: 'absolute',
			top: layout.insets.top + p.topGap,
			left: (layout.width - barWidth) / 2 + barWidth - m.portraitSlot,
		};
		plate = m.plate;
		gap = m.gap;
		items = ['sound', 'settings'];
	} else if (layout.phoneLandscape) {
		menuStyle = {
			position: 'absolute',
			top: Math.max(layout.insets.top, m.phoneTop),
			right: Math.max(layout.insets.right, DUEL_HUD_RAIL.phoneLeft) + m.phoneRightExtra,
		};
		plate = m.plate;
		gap = m.gap;
		items = ['notifications', 'sound', 'settings'];
	} else {
		menuStyle = { position: 'absolute', top: m.desktopTop, right: m.desktopRight };
		plate = m.plate * DUEL_HUD_DESKTOP_SCALE;
		gap = m.desktopGap;
		items = ['notifications', 'sound', 'settings'];
	}

	return (
		<View pointerEvents='box-none' style={StyleSheet.absoluteFill}>
			{layout.landscape ? (
				<RailHud {...props} layout={layout} />
			) : (
				<PortraitHud {...props} layout={layout} />
			)}
			{menuOpen ? (
				<Pressable
					sound={false}
					onPress={onMenuClose}
					accessibilityElementsHidden
					importantForAccessibility='no'
					style={StyleSheet.absoluteFill}
				/>
			) : null}
			<DuelMoreMenu
				open={menuOpen}
				onToggle={onMenuToggle}
				plate={plate}
				gap={gap}
				shellWhenClosed={layout.landscape}
				items={items}
				soundOn={soundOn}
				unreadCount={unreadCount}
				onSound={onSound}
				onGameSettings={onGameSettings}
				onNotifications={onNotifications}
				style={menuStyle}
			/>
		</View>
	);
};

const styles = StyleSheet.create({
	row: { flexDirection: 'row', alignItems: 'center' },
	column: { flexDirection: 'column' },
	centered: { alignItems: 'center', justifyContent: 'center' },
	centeredText: { textAlign: 'center' },
	grow: { flex: 1 },
	shrink: { flexShrink: 1 },
	counter: { flex: 1, overflow: 'hidden' },
	pill: {
		alignSelf: 'flex-start',
		borderRadius: 999,
		backgroundColor: duelHudColors.pillFill,
		borderColor: duelHudColors.pillBorder,
	},
	leave: {
		borderColor: duelHudColors.leaveBorder,
		shadowColor: duelHudColors.leaveGlow,
		shadowOpacity: 1,
		shadowOffset: { width: 0, height: 0 },
	},
	edgeLight: {
		position: 'absolute',
		backgroundColor: duelHudColors.accent,
		shadowColor: 'rgba(0,200,255,0.7)',
		shadowOpacity: 1,
		shadowRadius: 6,
		shadowOffset: { width: 0, height: 0 },
	},
	black: { fontFamily: DUEL_HUD_FONT, fontWeight: '900' },
	blackItalic: { fontFamily: DUEL_HUD_FONT, fontWeight: '900', fontStyle: 'italic' },
	bold: { fontFamily: DUEL_HUD_FONT, fontWeight: '700' },
	semibold: { fontFamily: DUEL_HUD_FONT, fontWeight: '600' },
	medium: { fontFamily: DUEL_HUD_FONT, fontWeight: '500' },
});
